/**
 * Birthday Gift Drop — cart gift auto-add (CNV-3010).
 *
 * A Shopify discount function decides the tier server-side and discounts the
 * earned gift to $0; it never adds anything. This file adds the gift line and
 * removes it when the cart stops earning it. Both halves read the same app
 * metafield and must agree, because the function is the only thing that can
 * make a gift free — a line it does not recognise is one the shopper is
 * charged for. So everything here fails closed.
 *
 * Gift work runs BEFORE the shopper's request is forwarded, from a preview of
 * what their payload will do to the cart we already hold. By the time anything
 * reads the cart back, the right gift is in it. Their request is forwarded
 * untouched, except that our removals ride along inside it (see rideRemovals).
 *
 * Load order is theme -> EasyGift -> us -> network (EasyGift defers after us in
 * theme.liquid), which means its writes pass through this tap and land in our
 * cache for free. Ours deliberately bypass it: EasyGift only skips requests
 * marked _easygift_internal or _fondue_internal, so going through window.fetch
 * would make it treat our gift add as a shopper action.
 */
(function () {
  'use strict';

  if (window.__birthdayGiftDrop) return;
  window.__birthdayGiftDrop = true;

  var SETTINGS = window.BIRTHDAY_GIFT_DROP_SETTINGS || {};
  var GIFT_PROPERTY = '_birthday_gift';

  var CART_MUTATION = /\/cart\/(add|change|update|clear)(\.js)?([\/?#]|$)/;
  var CART_READ = /\/cart\.js([?#]|$)/;
  var IS_ADD = /\/cart\/add(\.js)?([\/?#]|$)/;
  var IS_CHANGE = /\/cart\/change(\.js)?([\/?#]|$)/;
  var IS_UPDATE = /\/cart\/update(\.js)?([\/?#]|$)/;
  var IS_CLEAR = /\/cart\/clear(\.js)?([\/?#]|$)/;

  var DECLINED_STORAGE_KEY = 'birthday-gift-drop:declined';

  // Marks our own requests so this tap ignores them.
  var OURS = 'bgd_internal=1';
  var OURS_HEADER = 'X-BGD-Internal';

  // Another gift app's own write: real cart activity, but not shopper intent.
  var OTHER_APP_WRITE = /_easygift_internal=true|_fondue_internal=true/;

  // EasyGift's gift marker: never counted, never touched.
  var OTHER_GIFT_PROPERTY = '_Gifted';

  function ms(name, fallback) {
    return typeof SETTINGS[name] === 'number' ? SETTINGS[name] : fallback;
  }

  // Grace for an accepted add that no cart has shown yet, so a lagging read
  // cannot cause a second copy.
  var CONFIRM_MS = ms('confirmMs', 4000);

  // Failed adds before a gift variant is given up on for the session.
  var MAX_ADD_FAILURES = 2;

  var DEBUG =
    SETTINGS.debug === true ||
    String(window.location.search).indexOf('bgd_debug=1') !== -1;

  // The fetch as it was before this tap: used to forward what we intercept and
  // to send our own writes. See the header for why ours must not go through
  // window.fetch.
  var baseFetch = window.fetch;

  function log() {
    if (!DEBUG || !window.console) return;
    console.log.apply(
      console,
      ['[birthday-gift-drop]'].concat(Array.prototype.slice.call(arguments))
    );
  }

  function warn(message, detail) {
    if (!window.console) return;
    console.warn('[birthday-gift-drop] ' + message, detail === undefined ? '' : detail);
  }

  function noop() {}

  // Declared before the tap is installed: a cart request from any script below
  // us could reach a handler while this file is still running.
  var stats = {
    renders: 0,
    rendersThisAction: 0,
    reads: 0,
    mutations: 0,
    rode: 0, // shopper actions where gift work ran ahead of their request
    rodeRemovals: 0, // removals carried inside the shopper's own request
    passes: 0,
    added: 0,
    removed: 0
  };

  var acceptedAt = {};

  // Locale-prefixed markets serve the cart under e.g. /en-ca/cart.js.
  function cartRoot() {
    try {
      var root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
      return typeof root === 'string' ? root.replace(/\/$/, '') : '';
    } catch (e) {
      return '';
    }
  }

  // ---------------------------------------------------------------- config --

  var configEl = document.getElementById('birthday-gift-drop-config');
  if (!configEl) {
    log('no config element rendered — gate closed in Liquid');
    return;
  }

  var config;
  try {
    config = JSON.parse(configEl.textContent);
  } catch (e) {
    warn('config metafield is not valid JSON — doing nothing', e);
    return;
  }

  if (!config || !Array.isArray(config.tiers) || !config.tiers.length) {
    warn(
      'no tiers in the app theme-config shop metafield — doing nothing. ' +
        'Save the Birthday Gift Drop settings panel on the discount to publish a fresh copy.',
      { resolvedVia: SETTINGS.resolvedVia || null }
    );
    return;
  }

  // Mirrors the discount function's toPositiveInt.
  function toPositiveInt(value) {
    if (typeof value !== 'number' && typeof value !== 'string') return null;
    var parsed = Math.floor(Number(value));
    if (!isFinite(parsed) || parsed < 1) return null;
    return parsed;
  }

  // No usable minItems anywhere means the metafield still holds the old
  // price-shaped config. The function ignores it, so we must too.
  if (
    !config.tiers.some(function (tier) {
      return toPositiveInt(tier.minItems) !== null;
    })
  ) {
    warn(
      'no tier carries a usable minItems — the metafield still holds the old ' +
        'price-shaped config. Doing nothing until the Birthday Gift Drop ' +
        'settings panel is re-saved with item-count tiers.',
      { resolvedVia: SETTINGS.resolvedVia || null }
    );
    return;
  }

  // The config's campaign dates are a stale copy and are deliberately ignored:
  // the theme setting and theme publication are the switch.

  // ------------------------------------------------------------ gift index --

  function numericId(gid) {
    if (gid === null || gid === undefined) return null;
    var parsed = parseInt(String(gid).split('/').pop(), 10);
    return isNaN(parsed) ? null : parsed;
  }

  var giftVariantIds = {};
  var giftProductIds = {};

  config.tiers.forEach(function (tier) {
    (tier.otpGifts || []).concat(tier.subGifts || []).forEach(function (gift) {
      if (!gift) return;
      var variantId = numericId(gift.variantId);
      var productId = numericId(gift.productId);
      if (variantId) giftVariantIds[variantId] = true;
      if (productId) giftProductIds[productId] = true;
    });
  });

  if (!Object.keys(giftVariantIds).length) {
    warn('tiers contain no gift variant ids — doing nothing');
    return;
  }

  // A configured gift product however it got into the cart — broader than
  // isManagedLine, matching the discount function's exclusion.
  function isGiftProductLine(item) {
    return !!(giftVariantIds[item.id] || giftProductIds[item.product_id]);
  }

  // A line this script added: the only kind it may remove or re-quantify.
  function isManagedLine(item) {
    return !!(item.properties && item.properties[GIFT_PROPERTY]);
  }

  function isOtherAppGift(item) {
    return !!(item.properties && item.properties[OTHER_GIFT_PROPERTY]);
  }

  // ----------------------------------------------------- session decisions --

  function readSet(storageKey) {
    var map = {};
    try {
      var raw = window.sessionStorage.getItem(storageKey);
      if (!raw) return map;
      var list = JSON.parse(raw);
      if (!Array.isArray(list)) return map;
      list.forEach(function (id) {
        map[id] = true;
      });
    } catch (e) {
      /* sessionStorage unavailable (private mode, blocked cookies) */
    }
    return map;
  }

  function writeSet(storageKey, map) {
    try {
      window.sessionStorage.setItem(storageKey, JSON.stringify(Object.keys(map)));
    } catch (e) {
      /* non-fatal: the in-memory map still holds for this page */
    }
  }

  /**
   * Gifts the discount would not zero, so they were removed and are no longer
   * offered. Keyed by variant + tier + purchase type, because a variant refused
   * in one situation can be valid in another. In memory only — a refusal
   * surviving a reload is indistinguishable from the feature being broken.
   */
  var refused = {};

  function refusalKey(variantId, situation) {
    return variantId + '@' + situation;
  }

  function refuse(variantId, situation) {
    var key = refusalKey(variantId, situation);
    if (refused[key]) return;
    refused[key] = true;
    warn(
      'gift variant ' +
        variantId +
        ' was added but did not become free — removed it and stopped offering it ' +
        'for this tier. Check that the discount covers this gift for this cart.',
      { situation: situation }
    );
  }

  var addFailures = {};
  var unavailable = {};

  function noteAddFailure(variantId) {
    addFailures[variantId] = (addFailures[variantId] || 0) + 1;
    if (addFailures[variantId] < MAX_ADD_FAILURES || unavailable[variantId]) return;
    unavailable[variantId] = true;
    warn(
      'gift variant ' +
        variantId +
        ' could not be added to the cart — giving up on it for this session. ' +
        'Check that it is in stock and available on the online store.'
    );
  }

  // Gifts the shopper deleted themselves — never put straight back.
  var declined = readSet(DECLINED_STORAGE_KEY);

  // Line key -> variant id, so a removal on the wire can be attributed.
  var managedKeys = {};

  /**
   * A decline is only ever inferred from a shopper mutation naming the line.
   * Mere absence from a read could be a clear, a checkout or a rebuild, and
   * guessing wrong suppresses a gift the shopper is still owed.
   */
  function noteRemoval(key, quantity) {
    if (quantity !== 0) return;
    var variantId = managedKeys[key];
    if (!variantId || declined[variantId]) return;
    declined[variantId] = true;
    writeSet(DECLINED_STORAGE_KEY, declined);
    log('gift declined by shopper', variantId);
  }

  function inspectMutation(payload) {
    if (!payload) return;

    if (payload.updates && typeof payload.updates === 'object') {
      Object.keys(payload.updates).forEach(function (key) {
        noteRemoval(key, Number(payload.updates[key]));
      });
      return;
    }

    if (payload.quantity === undefined) return;

    if (payload.id !== undefined) {
      noteRemoval(String(payload.id), Number(payload.quantity));
      return;
    }

    // change.js can address a line by 1-based position instead of by key.
    if (payload.line !== undefined && cart) {
      var position = parseInt(payload.line, 10);
      var item = position >= 1 ? (cart.items || [])[position - 1] : null;
      if (item) noteRemoval(item.key, Number(payload.quantity));
    }
  }

  // ------------------------------------------------------------ tier maths --

  /**
   * Items-per-unit per variant, resolved by Liquid from the same metafields the
   * discount function reads (snippets/birthday-gift-per-unit.liquid) — /cart.js
   * exposes no metafields. Only counts above 1 are rendered; anything absent
   * counts as 1, which can only UNDERcount, and an undercounted gift is refused
   * by the function and removed here.
   */
  var perUnitByVariant = {};
  (function () {
    var countsEl = document.getElementById('birthday-gift-drop-item-counts');
    if (!countsEl) {
      log('no item-counts element rendered — every line counts as 1');
      return;
    }
    var raw;
    try {
      raw = JSON.parse(countsEl.textContent);
    } catch (e) {
      warn('item-counts blob is not valid JSON — every line counts as 1', e);
      return;
    }
    if (!raw || typeof raw !== 'object') return;
    Object.keys(raw).forEach(function (key) {
      var variantId = parseInt(key, 10);
      var perUnit = toPositiveInt(raw[key]);
      if (variantId && perUnit) perUnitByVariant[variantId] = perUnit;
    });
  })();

  function perUnitFor(variantId) {
    return perUnitByVariant[variantId] || 1;
  }

  /**
   * Qualifying item count = quantity x items-per-unit, the discount function's
   * exact basis; prices play no part. Excluded before counting: our gift lines
   * (counting one would move the tier it earns and the cart would never
   * settle), configured gift products, gift cards and other apps' gifts. An
   * excluded line can never flip hasSubscription either — a gift on a selling
   * plan must not switch the cart onto the subscription column.
   */
  function analyse(cartLike) {
    var itemCount = 0;
    var hasSubscription = false;

    ((cartLike && cartLike.items) || []).forEach(function (item) {
      if (isManagedLine(item)) return;
      if (isGiftProductLine(item)) return;
      if (item.gift_card) return;
      if (isOtherAppGift(item)) return;
      itemCount += item.quantity * perUnitFor(item.id);
      if (item.selling_plan_allocation) hasSubscription = true;
    });

    return { itemCount: itemCount, hasSubscription: hasSubscription };
  }

  // Largest reached minItems wins; ties keep the earlier tier; no upper bound.
  function tierIndexFor(itemCount) {
    var winner = -1;
    var winnerMin = 0;
    for (var i = 0; i < config.tiers.length; i++) {
      var min = toPositiveInt(config.tiers[i].minItems);
      if (min === null || min > itemCount || min <= winnerMin) continue;
      winner = i;
      winnerMin = min;
    }
    return winner;
  }

  // `situation` names the tier AND the purchase type, because the same variant
  // can be a valid gift in one and not the other. Refusals key off it.
  function resolve(cartLike) {
    var summary = analyse(cartLike);
    var none = { wanted: [], situation: null, items: summary.itemCount };

    if (summary.itemCount <= 0) return none;

    var tierIndex = tierIndexFor(summary.itemCount);
    if (tierIndex === -1) return none;

    var tier = config.tiers[tierIndex];
    var wanted = [];
    var seen = {};

    var gifts = summary.hasSubscription ? tier.subGifts : tier.otpGifts;
    (gifts || []).forEach(function (gift) {
      if (!gift) return;
      var variantId = numericId(gift.variantId);
      if (!variantId || seen[variantId]) return; // duplicate refs, one line
      seen[variantId] = true;
      wanted.push(variantId);
    });

    return {
      wanted: wanted,
      situation: tierIndex + (summary.hasSubscription ? ':sub' : ':otp'),
      items: summary.itemCount
    };
  }

  // ------------------------------------------------------------ cart cache --

  /**
   * The cart as we last knew it, fed from response BODIES rather than an extra
   * read: change/update/clear answer with the whole cart, and add.js answers
   * with the lines it created, which are merged by key.
   *
   * `cartIsFull` is true only while it came from a whole-cart body. Lines
   * merged from an add.js may carry a price the discount has not been allocated
   * to yet, and the revenue check must never judge on those.
   */
  var cart = null;
  var cartToken = null;
  var cartIsFull = false;

  function tokenBase(token) {
    return String(token).split('?')[0];
  }

  function isCartBody(body) {
    if (!body || !Array.isArray(body.items)) return false;
    return body.token !== undefined || body.item_count !== undefined;
  }

  function reindex() {
    managedKeys = {};
    ((cart && cart.items) || []).forEach(function (item) {
      if (!isManagedLine(item)) return;
      managedKeys[item.key] = item.id;
      delete acceptedAt[item.id]; // seen it, no longer merely on its way
    });
  }

  /**
   * Our own reads are authoritative and may claim the token (Shopify mints a
   * new one when the first mutation creates the real cart). Bodies seen on
   * other scripts' requests can belong to a DIFFERENT cart — EasyGift builds a
   * duplicate one — so those are trusted only on a token match.
   */
  function adopt(body, authoritative) {
    if (!isCartBody(body)) return null;
    if (authoritative) {
      if (body.token) cartToken = tokenBase(body.token);
    } else if (!cartToken || !body.token || tokenBase(body.token) !== cartToken) {
      log('ignoring a cart response that is not ours', body.token);
      return null;
    }
    cart = body;
    cartIsFull = true;
    reindex();
    return cart;
  }

  function mergeAddedLines(body) {
    if (!cart || !Array.isArray(cart.items)) return;
    var lines = Array.isArray(body && body.items) ? body.items : body && body.key ? [body] : [];
    if (!lines.length) return;

    lines.forEach(function (line) {
      if (!line || !line.key) return;
      var found = -1;
      for (var i = 0; i < cart.items.length; i++) {
        if (cart.items[i].key === line.key) {
          found = i;
          break;
        }
      }
      if (found > -1) cart.items[found] = line;
      else cart.items.push(line);
    });
    cartIsFull = false;
    reindex();
  }

  function absorb(res, authoritative) {
    return peek(res).then(function (body) {
      if (isCartBody(body)) adopt(body, authoritative);
      else mergeAddedLines(body);
      return body;
    });
  }

  // -------------------------------------------------------------- cart i/o --

  function mark(url) {
    return url + (url.indexOf('?') === -1 ? '?' : '&') + OURS;
  }

  function isOurs(url, init) {
    if (String(url).indexOf(OURS) !== -1) return true;
    var headers = init && init.headers;
    if (!headers) return false;
    try {
      if (typeof headers.get === 'function') return !!headers.get(OURS_HEADER);
      return !!(headers[OURS_HEADER] || headers[OURS_HEADER.toLowerCase()]);
    } catch (e) {
      return false;
    }
  }

  /** Read a response body without consuming the caller's copy. */
  function peek(res) {
    if (!res || typeof res.clone !== 'function') return Promise.resolve(null);
    try {
      return res
        .clone()
        .json()
        .catch(function () {
          return null;
        });
    } catch (e) {
      return Promise.resolve(null);
    }
  }

  var writesInFlight = 0;

  function send(path, options) {
    var out = options || {};
    out.headers = out.headers || {};
    try {
      out.headers[OURS_HEADER] = '1';
    } catch (e) {
      /* a Headers instance that refuses index assignment — the URL marker holds */
    }
    return baseFetch.call(window, mark(cartRoot() + path), out);
  }

  function post(path, payload) {
    writesInFlight++;
    return send(path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload)
    }).then(
      function (res) {
        writesInFlight = Math.max(0, writesInFlight - 1);
        if (!res || !res.ok) {
          var error = new Error(path + ' ' + (res && res.status));
          error.status = res && res.status;
          throw error;
        }
        return absorb(res, true).then(function () {
          return res;
        });
      },
      function (error) {
        writesInFlight = Math.max(0, writesInFlight - 1);
        throw error;
      }
    );
  }

  function readCart() {
    return send('/cart.js', {
      method: 'GET',
      headers: { Accept: 'application/json' },
      cache: 'no-store'
    }).then(function (res) {
      if (!res || !res.ok) throw new Error('cart.js ' + (res && res.status));
      return absorb(res, true).then(function () {
        return cart;
      });
    });
  }

  function giftLine(variantId) {
    var properties = {};
    properties[GIFT_PROPERTY] = '1';
    return { id: variantId, quantity: 1, properties: properties };
  }

  function addGifts(variantIds) {
    return post('/cart/add.js', { items: variantIds.map(giftLine) }).then(
      function (res) {
        var now = Date.now();
        variantIds.forEach(function (id) {
          acceptedAt[id] = now;
          addFailures[id] = 0;
          stats.added++;
        });
        return res;
      },
      function (error) {
        if (variantIds.length < 2) {
          noteAddFailure(variantIds[0]);
          // One retry: a gift add can lose a race with another app writing to
          // the same cart, and nothing comes back for it until the shopper acts.
          if (addFailures[variantIds[0]] < MAX_ADD_FAILURES) {
            log('gift add failed — trying once more', variantIds[0]);
            return addGifts(variantIds);
          }
          throw error;
        }
        // A multi-item add is all-or-nothing, so one unavailable gift sinks the
        // whole request. Retry them one at a time rather than lose the rest.
        log('batched gift add failed — retrying one at a time', error);
        return Promise.all(
          variantIds.map(function (id) {
            return addGifts([id]).catch(noop);
          })
        );
      }
    );
  }

  function applyUpdates(updates) {
    return post('/cart/update.js', { updates: updates }).then(function (res) {
      stats.removed += Object.keys(updates).length;
      return res;
    });
  }

  // Note: this module never asks the cart UI to repaint. Every write happens
  // either just before a shopper request that repaints by itself, or at page
  // load with the drawer shut. Requesting a repaint means being woken by it.

  // -------------------------------------------------------------- preview --

  /**
   * The cart a pending request is about to produce, from the cart we hold plus
   * the request's payload. No network — everything the tier needs is in the
   * payload, and bundle item counts come from the Liquid-rendered map. Null
   * when it cannot be known, which just means we do nothing this time.
   */
  function shallow(item) {
    var copy = {};
    Object.keys(item).forEach(function (key) {
      copy[key] = item[key];
    });
    return copy;
  }

  function previewAdd(items, payload) {
    var lines = Array.isArray(payload.items)
      ? payload.items
      : payload.id !== undefined
        ? [payload]
        : [];
    if (!lines.length) return null;

    var out = items.slice();
    for (var i = 0; i < lines.length; i++) {
      var id = parseInt(lines[i].id, 10);
      if (!id) return null;
      var quantity = Number(lines[i].quantity);
      if (!isFinite(quantity) || quantity < 1) quantity = 1;
      var plan = lines[i].selling_plan ? { selling_plan: { id: lines[i].selling_plan } } : null;

      // Shopify merges an add into an existing line when variant, properties
      // and selling plan all match. Missing that would double-count and jump a
      // tier.
      var merged = false;
      for (var j = 0; j < out.length; j++) {
        var existing = out[j];
        if (existing.id !== id) continue;
        if (isManagedLine(existing) || isOtherAppGift(existing)) continue;
        if (!!existing.selling_plan_allocation !== !!plan) continue;
        var clone = shallow(existing);
        clone.quantity = existing.quantity + quantity;
        out[j] = clone;
        merged = true;
        break;
      }
      if (merged) continue;

      out.push({
        id: id,
        product_id: 0,
        key: 'preview:' + id + ':' + i,
        quantity: quantity,
        properties: lines[i].properties || null,
        gift_card: false,
        selling_plan_allocation: plan,
        final_line_price: 0
      });
    }
    return out;
  }

  function previewQuantities(items, wanted) {
    var out = [];
    items.forEach(function (item) {
      var quantity = wanted(item);
      if (quantity === null) {
        out.push(item);
        return;
      }
      if (!isFinite(quantity) || quantity <= 0) return; // removed
      var clone = shallow(item);
      clone.quantity = quantity;
      out.push(clone);
    });
    return out;
  }

  /** The line a payload addresses, by key, variant id or 1-based position. */
  function targetKey(items, payload) {
    if (payload.id !== undefined) {
      var target = String(payload.id);
      var key = null;
      items.forEach(function (item) {
        if (key === null && (item.key === target || String(item.id) === target)) key = item.key;
      });
      return key;
    }
    if (payload.line !== undefined) {
      var position = parseInt(payload.line, 10);
      var item = position >= 1 ? items[position - 1] : null;
      return item ? item.key : null;
    }
    return null;
  }

  function preview(url, payload) {
    if (IS_CLEAR.test(url)) return { items: [] };
    if (!cart || !Array.isArray(cart.items) || !payload) return null;
    var items = cart.items;

    if (IS_ADD.test(url)) {
      var added = previewAdd(items, payload);
      return added ? { items: added } : null;
    }

    if (IS_UPDATE.test(url)) {
      if (!payload.updates || typeof payload.updates !== 'object') return null;
      return {
        items: previewQuantities(items, function (item) {
          if (payload.updates[item.key] !== undefined) return Number(payload.updates[item.key]);
          if (payload.updates[item.id] !== undefined) return Number(payload.updates[item.id]);
          return null;
        })
      };
    }

    if (IS_CHANGE.test(url)) {
      if (payload.quantity === undefined) return null;
      var key = targetKey(items, payload);
      if (!key) return null;
      var quantity = Number(payload.quantity);

      var out = previewQuantities(items, function (item) {
        return item.key === key ? quantity : null;
      });

      // change.js can also move a line onto or off a selling plan, flipping the
      // cart between the one-time and subscription gift columns.
      if (payload.selling_plan !== undefined && quantity > 0) {
        out.forEach(function (item, index) {
          if (item.key !== key) return;
          var clone = shallow(item);
          clone.selling_plan_allocation = payload.selling_plan
            ? { selling_plan: { id: payload.selling_plan } }
            : null;
          out[index] = clone;
        });
      }
      return { items: out };
    }

    return null;
  }

  // --------------------------------------------------------------- planner --

  /**
   * What this cart should look like, as ops. A cart that already matches
   * produces nothing, which is what makes a quantity change that does not cross
   * a tier cost no requests at all.
   *
   * `audit` enables the revenue check, which reads prices and so is only
   * meaningful against a whole-cart body.
   */
  function planFor(cartLike, audit) {
    var resolved = resolve(cartLike);
    var situation = resolved.situation;
    var items = (cartLike && cartLike.items) || [];
    var now = Date.now();

    var earned = {};
    resolved.wanted.forEach(function (id) {
      earned[id] = true;
    });

    // "On its way" only means anything for a gift this cart still wants. An
    // entry stranded by a tier change would suppress that gift for the rest of
    // the session, which is what made rapid switching lose a tier.
    Object.keys(acceptedAt).forEach(function (id) {
      if (!earned[Number(id)]) delete acceptedAt[id];
    });

    if (audit && cartIsFull) {
      var linesPer = {};
      items.forEach(function (item) {
        if (isManagedLine(item)) linesPer[item.id] = (linesPer[item.id] || 0) + 1;
      });

      // EasyGift needs no equivalent of this: it prices its own gift, ours is
      // only free because the discount function zeroes it. A managed line still
      // carrying a price is one the shopper is being charged for. Duplicates
      // and unearned leftovers are priced for mundane reasons and are removed
      // by the ordinary diff below without prejudice.
      items.forEach(function (item) {
        if (!isManagedLine(item)) return;
        if (linesPer[item.id] !== 1 || !earned[item.id]) return;
        if (item.final_line_price > 0) refuse(item.id, situation);
      });
    }

    var outstanding = resolved.wanted.filter(function (id) {
      return !refused[refusalKey(id, situation)] && !declined[id] && !unavailable[id];
    });

    var updates = {};
    var keep = {};

    items.forEach(function (item) {
      if (!isManagedLine(item)) return;
      if (keep[item.id] || outstanding.indexOf(item.id) === -1) {
        updates[item.key] = 0; // unearned, refused, declined, or a duplicate
        return;
      }
      keep[item.id] = true;
      // One gift per order: the function's candidate is qty 1, so a line above
      // that is not covered.
      if (item.quantity > 1) updates[item.key] = 1;
    });

    var adds = [];
    outstanding.forEach(function (id) {
      if (keep[id]) return;
      var accepted = acceptedAt[id];
      if (accepted && now - accepted < CONFIRM_MS) return; // already on its way
      if (accepted) delete acceptedAt[id];
      adds.push(id);
    });

    var removals = Object.keys(updates);

    return {
      updates: updates,
      adds: adds,
      hasWork: removals.length > 0 || adds.length > 0,
      summary: {
        items: resolved.items,
        situation: situation,
        wanted: resolved.wanted,
        remove: removals.length,
        add: adds.length
      }
    };
  }

  /**
   * Removals first, then adds: a cart briefly holding no gift costs nothing,
   * while one briefly holding a gift the discount does not cover is a cart the
   * shopper could check out and be charged for.
   *
   * Strictly sequential. Concurrent Cart AJAX writes on this store return 200
   * and are silently discarded — measured at roughly a 40% loss rate — so this
   * ordering is a correctness requirement, not caution.
   */
  function applyPlan(plan, label) {
    log(label + ': applying', plan.summary);
    stats.passes++;

    var ops = Promise.resolve();
    if (plan.summary.remove) {
      ops = ops.then(function () {
        return applyUpdates(plan.updates);
      });
    }
    if (plan.adds.length) {
      ops = ops.then(function () {
        return addGifts(plan.adds);
      });
    }
    return ops.catch(function (error) {
      log(label + ': a gift op failed', error);
    });
  }

  // --------------------------------------------------------- serialisation --

  var chain = Promise.resolve();

  function enqueue(work) {
    var result = chain.then(function () {
      return work();
    });
    chain = result.then(noop, noop);
    return result;
  }

  /**
   * Reconcile against a freshly read cart. Only two callers, both one-shot
   * boundaries that cannot re-enter: page load and the moment before checkout.
   *
   * Two rounds at most. The second exists so a gift added in the first can be
   * seen once in a whole cart, which is the only way to tell whether the
   * discount actually zeroed it.
   */
  function reconcile(label) {
    function round(depth) {
      return readCart().then(function (fresh) {
        if (!fresh) return null;
        var plan = planFor(fresh, true);
        if (!plan.hasWork) {
          log(label + ': nothing to do', plan.summary);
          return null;
        }
        if (depth >= 2) {
          warn('the cart will not settle on the gift it has earned', plan.summary);
          return null;
        }
        return applyPlan(plan, label).then(function () {
          return round(depth + 1);
        });
      });
    }

    return enqueue(function () {
      return round(0).catch(function (error) {
        log(label + ': failed', error);
      });
    });
  }

  // ------------------------------------------------------------------- tap --

  function requestUrl(input) {
    if (typeof input === 'string') return input;
    if (input && typeof input.url === 'string') return input.url;
    if (input && typeof input.href === 'string') return input.href;
    return String(input || '');
  }

  /** Cart request bodies arrive as JSON or as form encoding. */
  function parseBody(body) {
    if (!body || typeof body !== 'string') return null;
    try {
      return JSON.parse(body);
    } catch (e) {
      /* not JSON — try form encoding */
    }
    try {
      var params = new URLSearchParams(body);
      var payload = {};
      var seen = false;
      params.forEach(function (value, key) {
        payload[key] = value;
        seen = true;
      });
      return seen ? payload : null;
    } catch (e2) {
      return null;
    }
  }

  var mutationsInFlight = 0;

  /**
   * Set when we committed gift state from a preview and the shopper's own
   * response could not confirm it — an add.js answer lists the lines it
   * created, not the cart. The next authoritative whole-cart read settles it,
   * so the confirmation rides the read the theme already makes rather than
   * costing one of ours.
   */
  var awaitingConfirmation = false;

  /**
   * Carry our removals inside the shopper's own request so they cost no write.
   * A removal is only "line key, quantity 0", which an update.js `updates` map
   * expresses exactly — so a tier swap needs one write of ours, not two.
   *
   * An update.js request has its map extended. A change.js is rewritten into
   * the equivalent update.js: verified on this store that update.js addressed
   * by line key behaves identically, INCLUDING preserving a selling plan, which
   * a quantity click on a subscription line depends on.
   *
   * Refused when the payload holds anything update.js cannot express, or when
   * the body is not plain JSON. The add can never ride: creating our line needs
   * the _birthday_gift property, and only add.js carries properties.
   *
   * Mutates `options`, returns a replacement URL when one is needed, and takes
   * the ridden keys out of the plan so they are not written twice.
   */
  function rideRemovals(url, options, parsed, plan) {
    if (!parsed) return null;
    var zeros = Object.keys(plan.updates).filter(function (key) {
      return plan.updates[key] === 0; // a clamp back to 1 stays in our own write
    });
    if (!zeros.length) return null;

    function take(updates, skipKey) {
      var rode = 0;
      zeros.forEach(function (key) {
        if (key === skipKey) return; // never overwrite what the shopper asked
        updates[key] = 0;
        delete plan.updates[key];
        rode++;
      });
      plan.summary.remove = Object.keys(plan.updates).length;
      return rode;
    }

    if (IS_UPDATE.test(url) && parsed.updates && typeof parsed.updates === 'object') {
      if (!take(parsed.updates, null)) return null;
      options.body = JSON.stringify(parsed);
      log('removals riding in the shopper update');
      return { url: null };
    }

    if (IS_CHANGE.test(url)) {
      if (parsed.selling_plan !== undefined || parsed.properties !== undefined) return null;
      if (parsed.quantity === undefined) return null;
      var key = targetKey((cart && cart.items) || [], parsed);
      if (!key) return null;

      var updates = {};
      updates[key] = Number(parsed.quantity);
      if (!take(updates, key)) return null;

      options.method = 'POST';
      options.headers = { 'Content-Type': 'application/json', Accept: 'application/json' };
      options.body = JSON.stringify({ updates: updates });
      log('change rewritten to an update carrying our removal', updates);

      // Derived from the original URL, not hardcoded: locale-prefixed markets
      // serve this as /en-ca/cart/change.js and the prefix must survive.
      return {
        url: url.replace(/\/cart\/change(\.js)?/, function (match, js) {
          return '/cart/update' + (js || '.js');
        })
      };
    }

    return null;
  }

  /**
   * A cart mutation. Gift work runs before the shopper's request is forwarded,
   * so the cart already holds the right gift by the time their change lands.
   */
  function handleMutation(self, input, init, url) {
    stats.mutations++;
    mutationsInFlight++;

    // Our own copy of the options: rideRemovals rewrites the body.
    var options = {};
    if (init) {
      Object.keys(init).forEach(function (key) {
        options[key] = init[key];
      });
    }
    var target = input;

    // Set once we commit gift state from a PREVIEW of what this request will
    // do. That prediction is only worth checking if we acted on it.
    var wroteAhead = false;

    function forward() {
      return baseFetch.call(self, target, options).then(
        function (res) {
          mutationsInFlight = Math.max(0, mutationsInFlight - 1);
          if (res && res.ok) absorb(res, false);
          return res;
        },
        function (error) {
          mutationsInFlight = Math.max(0, mutationsInFlight - 1);
          throw error;
        }
      );
    }

    // Another app's write: forward untouched, just keep our cart current.
    if (OTHER_APP_WRITE.test(url)) return forward();

    stats.rendersThisAction = 0;
    var rawBody = init && typeof init.body === 'string' ? init.body : null;
    var payload = parseBody(rawBody);
    inspectMutation(payload);
    // Only a body that arrived as JSON can be safely re-encoded as JSON.
    var jsonBody = payload && rawBody.charAt(0) === '{' ? payload : null;

    var work = enqueue(function () {
      var projected = preview(url, payload);
      if (!projected) {
        log('cannot tell what this request will do — leaving it', url);
        return null;
      }
      var plan = planFor(projected, true);
      if (!plan.hasWork) return null;
      stats.rode++;

      var ridden = null;
      try {
        ridden = rideRemovals(url, options, jsonBody, plan);
      } catch (e) {
        log('could not put the removal in the shopper request', e);
      }
      if (ridden) {
        stats.rodeRemovals++;
        if (ridden.url) target = ridden.url;
      }

      if (!plan.summary.remove && !plan.adds.length) return null;
      wroteAhead = true;
      return applyPlan(plan, 'ahead of the shopper request');
    });

    /**
     * The shopper's own write can be discarded by the platform while still
     * answering 200 — the same silent loss that makes concurrent cart writes
     * unsafe here. When that happens the gift we already committed belongs to a
     * cart that never came into existence: a tier-2 gift sitting on a one-item
     * cart, which the discount function refuses and the shopper is charged for.
     *
     * So when we wrote ahead, confirm the cart the server ACTUALLY produced
     * still earns what we put in it, and put it right before the theme sees
     * anything. This runs inside the promise the theme is awaiting, so the
     * drawer still paints exactly once, from the corrected cart — nothing
     * renders and then changes. Our correction goes out marked internal, which
     * the theme never sees, so it cannot trigger a render of its own.
     *
     * Only reached when we acted on a prediction. A request we left alone made
     * no prediction worth checking and costs nothing here.
     */
    /**
     * Apply whatever the cart in hand now earns, serialised with every other
     * gift write, and optionally hand the corrected cart back to the theme.
     */
    function correct(res, substitute) {
      return enqueue(function () {
        var plan = planFor(cart, true);
        if (!plan.hasWork) return false;
        warn(
          'the shopper request did not land as predicted — correcting the ' +
            'gift before the cart renders',
          plan.summary
        );
        return applyPlan(plan, 'after the shopper request').then(function () {
          return true;
        });
      }).then(function (corrected) {
        if (!corrected || !substitute) return res;
        // The last write of a correction is often an add, and add.js answers
        // with only the lines it created — the cart we hold is then a MERGE,
        // whose totals belong to the cart before the add. Handing that to the
        // theme would paint a wrong price, which is a worse failure than the
        // one being fixed. Only ever substitute a cart the server gave whole.
        return (cartIsFull ? Promise.resolve(cart) : readCart()).then(function (full) {
          if (!isCartBody(full)) return res;
          return new Response(JSON.stringify(full), {
            status: 200,
            headers: { 'Content-Type': 'application/json' }
          });
        });
      });
    }

    function settle(res) {
      if (!wroteAhead) return res;

      // A request that failed outright is the clearest case of all: the cart
      // did not change, so a gift written for the projected cart is certainly
      // wrong. The theme does not reliably read the cart back after a failure,
      // so this one cannot be left to handleRead — and the response stands as
      // it is, because the theme's own error handling has to see the failure.
      if (!res || !res.ok) {
        return readCart()
          .then(function () {
            return correct(res, false);
          })
          .catch(function (error) {
            log('could not settle after a failed shopper request', error);
            return res;
          });
      }

      var wasWholeCart = false;

      return peek(res)
        .then(function (body) {
          // change/update/clear answer with the whole cart, so the check is
          // pure local arithmetic and costs nothing.
          wasWholeCart = isCartBody(body);
          if (wasWholeCart) return adopt(body, true);

          // add.js answers with only the lines it created, which cannot show
          // whether the rest of the cart survived. Rather than spend a read of
          // our own, hand the job to the read the theme already makes straight
          // after an add — see handleRead. Correct runs then cost nothing here
          // either.
          awaitingConfirmation = true;
          return null;
        })
        .then(function (fresh) {
          if (!fresh) return res;
          // A whole-cart answer is what the theme paints from, so hand back the
          // corrected one. An add.js answer is a list of created lines and the
          // theme reads the cart back itself, which handleRead settles.
          return correct(res, wasWholeCart);
        })
        .catch(function (error) {
          log('could not settle the cart after the shopper request', error);
          return res;
        });
    }

    // A failure of ours must never stop the shopper's request going out.
    return work.then(forward, forward).then(settle, function (error) {
      // The request REJECTED — dropped connection, timeout, an abort. Nothing
      // reached the server, so the cart certainly did not change and the gift
      // written for the projected cart is certainly wrong. Settle it, then
      // re-throw: the theme has to see its own request fail.
      var rethrow = function () {
        throw error;
      };
      return settle(null).then(rethrow, rethrow);
    });
  }

  /**
   * A cart read. Normally just used to keep the cart we hold current.
   *
   * It is also where an add settles. An add.js response cannot show whether
   * the rest of the cart survived, and the theme reads the cart back straight
   * after every add — so that read is a whole, authoritative cart arriving for
   * free. If the gift we committed from the preview does not match it, put it
   * right and hand back the corrected cart, inside the promise the theme is
   * already awaiting. The drawer still paints once, from the corrected state.
   */
  function handleRead(self, input, init, url) {
    stats.reads++;
    // EasyGift reads its duplicate cart through here too; adopting that as
    // authoritative would re-point our token at the wrong cart.
    var authoritative = !OTHER_APP_WRITE.test(url);
    return baseFetch.call(self, input, init).then(function (res) {
      if (!res || !res.ok) return res;
      var absorbed = absorb(res, authoritative);
      if (!authoritative || !awaitingConfirmation) return res;
      awaitingConfirmation = false;

      return absorbed
        .then(function (body) {
          if (!isCartBody(body) || !cartIsFull) return res;

          // Queued for the same reason as settle(): a correction that writes
          // off the chain can overlap the next action's write.
          return enqueue(function () {
            var plan = planFor(cart, true);
            if (!plan.hasWork) return false;
            warn(
              'the add did not land as predicted — correcting the gift before ' +
                'the cart renders',
              plan.summary
            );
            return applyPlan(plan, 'settling the add on the theme read').then(
              function () {
                return true;
              }
            );
          }).then(function (corrected) {
            if (!corrected) return res;
            // Same rule as settle(): only ever hand back a cart the server
            // gave us whole, never a merge whose totals are stale.
            return (cartIsFull ? Promise.resolve(cart) : readCart()).then(
              function (full) {
                if (!isCartBody(full)) return res;
                return new Response(JSON.stringify(full), {
                  status: 200,
                  headers: { 'Content-Type': 'application/json' }
                });
              }
            );
          });
        })
        .catch(function (error) {
          log('could not settle the add on the cart read', error);
          return res;
        });
    });
  }

  window.fetch = function (input, init) {
    var url = requestUrl(input);

    if (isOurs(url, init)) return baseFetch.apply(this, arguments);
    if (CART_MUTATION.test(url)) return handleMutation(this, input, init, url);
    if (CART_READ.test(url)) return handleRead(this, input, init, url);

    return baseFetch.apply(this, arguments);
  };

  // Nothing in this theme uses XHR for cart calls, but anything that does still
  // has to be noticed. An XHR cannot be held open while we write, so this one
  // reconciles afterwards instead.
  var priorOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    var href = String(url || '');
    this.__bgdCartMutation = CART_MUTATION.test(href) && href.indexOf(OURS) === -1;
    return priorOpen.apply(this, arguments);
  };

  var priorSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.send = function (body) {
    if (this.__bgdCartMutation) {
      stats.mutations++;
      inspectMutation(parseBody(body));
      var done = false;
      this.addEventListener('loadend', function () {
        if (done) return;
        done = true;
        reconcile('xhr');
      });
    }
    return priorSend.apply(this, arguments);
  };

  /**
   * sidecart.js hands over the cart it just painted. We take the cart and
   * nothing else — no decision, no write, no repaint request. A listener that
   * writes can be woken by the repaint it causes; this one cannot.
   */
  document.addEventListener('mozi:cart:updated', function (event) {
    stats.renders++;
    stats.rendersThisAction++;
    var painted = event && event.detail && event.detail.cart;
    if (isCartBody(painted)) adopt(painted, true);
    log('cart rendered', { rendersThisAction: stats.rendersThisAction });
  });

  // --------------------------------------------------------- checkout gate --

  var CHECKOUT_HOLD_MS = ms('checkoutHoldMs', 2500);

  function busy() {
    return writesInFlight > 0 || mutationsInFlight > 0;
  }

  // Synchronous and free. Since the render listener no longer acts, a checkout
  // press is the last boundary before the shopper's money is involved.
  function hasPricedGift() {
    return ((cart && cart.items) || []).some(function (item) {
      return isManagedLine(item) && item.final_line_price > 0;
    });
  }

  document.addEventListener(
    'click',
    function (event) {
      if (!busy() && !hasPricedGift()) return;
      var target =
        event.target && event.target.closest
          ? event.target.closest(
              '#sidecart-checkout, a[href^="/checkout"], button[name="checkout"], input[name="checkout"]'
            )
          : null;
      if (!target) return;

      event.preventDefault();
      event.stopPropagation();
      log('holding checkout until the cart is quiet');

      if (hasPricedGift()) reconcile('checkout');

      var started = Date.now();
      (function waitQuiet() {
        if (!busy() || Date.now() - started > CHECKOUT_HOLD_MS) {
          window.location.href = cartRoot() + '/checkout';
          return;
        }
        setTimeout(waitQuiet, 100);
      })();
    },
    true
  );

  // -------------------------------------------------------------- exports --

  var thresholds = [];
  config.tiers.forEach(function (tier) {
    var min = toPositiveInt(tier.minItems);
    if (min !== null) thresholds.push(min);
  });
  thresholds.sort(function (a, b) {
    return a - b;
  });

  /**
   * Read-only view for the progress bars (CNV-3174), using the same counting
   * this module acts on so a bar can never promise a gift the cart logic would
   * not add. Absent whenever this module bailed, which the bars read as "stay
   * on the stock UI".
   */
  window.BirthdayGiftDrop = {
    thresholds: thresholds,
    summarise: function (cartLike) {
      var summary = analyse(cartLike || {});
      return {
        itemCount: summary.itemCount,
        hasSubscription: summary.hasSubscription,
        thresholds: thresholds
      };
    },
    stats: stats,
    reconcileNow: function () {
      return reconcile('manual');
    }
  };

  log('active', { resolvedVia: SETTINGS.resolvedVia || null, config: config });

  // One reconcile at load: a qualifying cart gets its gift and a stale one is
  // tidied. It also primes the cart we hold, so the first shopper action can be
  // decided without a read.
  function start() {
    reconcile('load');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
